import React, { lazy, useEffect, useLayoutEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Empty,
  Form,
  InputNumber,
  Row,
  Select,
} from 'antd';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

import './purchaseItemTags.styles.less';
import moment from 'moment';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import Barcode from 'react-barcode';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  pageSizesType,
  pageTypeSizeOptions,
  pageTypeSizes,
} from '@/types/PageSize';
import { useQueryParams } from '@/hooks/useQueryParams';
import { usePurchaseItemsTags } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { usePurchase } from '@/core/domain/purchase/Purchase.hooks';
import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';

let prevSliceCount = 0;

export interface CustomerTagFormValues {
  cols: number;
  gap: number;
  pageTypeSize: string;
  pageSize: { x: number; y: number };
  orientation: 'landscape' | 'portrait';
  lineBreak: number;
  noLineBreak: boolean;
  textSize: number;
  descriptionSize: number;
  withCardBorder: boolean;
  contentColumns: number;
  cardHeight: number;
  cardHeightAuto: boolean;
  margin: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  textAlign: 'left' | 'center' | 'right';
  showSaleValue: boolean;
  issueDate:
    | 'DD/MM/YYYY HH:mm:ss'
    | 'DD/MM/YYYY HH:mm'
    | 'DD/MM/YYYY HH'
    | 'DD/MM/YYYY'
    | 'DD/MM/YY'
    | 'None';
  showCompanyImage: boolean;
  companyImageWidth: undefined | number;
  companyImageHeight: undefined | number;
  companyImageResponsive: boolean;
  companyImage: string;
}

const initialState = {
  cols: 3,
  gap: 5,
  lineBreak: 4,
  pageTypeSize: 'A4',
  pageSize: pageSizesType['A4'],
  orientation: 'portrait',
  noLineBreak: true,
  textSize: 16,
  descriptionSize: 30,
  withCardBorder: true,
  contentColumns: 1,
  displayBarcode: true,
  cardHeightAuto: true,
  cardHeight: 60,
  margin: 10,
  marginTop: 10,
  marginBottom: 10,
  marginLeft: 10,
  marginRight: 10,
  textAlign: 'left',
  showSaleValue: true,
  issueDate: 'DD/MM/YYYY HH:mm:ss',
  showCompanyImage: true,
  companyImageWidth: undefined,
  companyImageHeight: 80,
  companyImageResponsive: false,
  companyImage: undefined,
};

export interface CustomerTagList {
  id: string;
  name: string;
  address: string;
}

const STORAGE_KEY = 'purchaseTagsConfig';

const initialStorage = localStorage.getItem(STORAGE_KEY);
const initialFormValues = {
  ...initialState,
  ...(initialStorage ? JSON.parse(initialStorage) : {}),
};

const SLICE_COUNT = 50;

const PrivateReportPurchaseItemTags: React.FC = () => {
  const match = useRouteMatch();

  const [drawerConfigOpen, drawerConfigAction] = useDisclosure(false);

  const [form] = Form.useForm<CustomerTagFormValues | undefined>();
  const formValues = Form.useWatch([], form);

  const hasPageSizeTypeCustom =
    formValues?.pageTypeSize === pageTypeSizes['Custom'];
  const isLandscape = formValues?.orientation === 'landscape';

  const colWidth = 100 / (formValues?.cols || 0);

  const colWidthCalc = `calc(${colWidth}% - ${formValues?.gap || 0}mm)`;

  document.title = `etiquetas_dos_itens_compra-${moment().format(
    'DDMMYYYY_HHmmss',
  )}`;

  const { purchaseId } = useQueryParams<{ purchaseId?: string }>();

  const purchaseDatail = usePurchase(Number(purchaseId));

  const companyDetail = useCompany(purchaseDatail.data?.companyId);

  const { data: purchaseData, isLoading } = usePurchaseItemsTags(
    Number(purchaseId),
    !!purchaseId,
  );

  const items = purchaseData?.data;

  useLayoutEffect(() => {
    const css = `
      @media print {
        .print-area {
          border: none !important;
          padding: 0px !important;
          width: 100% !important;
        }


        @page {
          size: ${
            hasPageSizeTypeCustom
              ? `${formValues?.pageSize[isLandscape ? 'y' : 'x'] || 0}mm ${
                  formValues?.pageSize[isLandscape ? 'x' : 'y'] || 0
                }mm`
              : formValues?.pageTypeSize || 'auto'
          } ${!hasPageSizeTypeCustom ? formValues?.orientation : ''};

          margin-top: ${formValues?.marginTop || 0}mm;
          margin-bottom: ${formValues?.marginBottom || 0}mm;
          margin-left: ${formValues?.marginLeft || 0}mm;
          margin-right: ${formValues?.marginRight || 0}mm;
        }
    `;

    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');

    head.appendChild(style);

    style.setAttribute('type', 'text/css');
    style.setAttribute('id', 'purchaseItem-tag-style-print');

    style.appendChild(document.createTextNode(css));

    return () =>
      document.getElementById('purchaseItem-tag-style-print')?.remove();
  }, [formValues]);

  useLayoutEffect(() => {
    if (!formValues?.noLineBreak && items?.length) {
      const printAreaElem = document.getElementById('print-area');

      if (!!printAreaElem?.firstChild?.childNodes.length) {
        Array.from(printAreaElem.firstChild?.childNodes)
          .filter(
            (item) =>
              !(item as HTMLDivElement).classList.contains('break-line'),
          )
          .forEach((_, i, items) => {
            if (
              !(
                (i + 1) %
                ((formValues?.cols || 0) * (formValues?.lineBreak || 0))
              )
            ) {
              const breakLineDiv = document.createElement('div');

              breakLineDiv.classList.add('break-line');
              breakLineDiv.style.marginBottom = `${formValues?.gap}mm`;

              printAreaElem.firstChild?.insertBefore(
                breakLineDiv,
                items[i + 1],
              );
            }
          });
      }
    }

    return () => {
      document.querySelectorAll('.break-line').forEach((node) => node.remove());
    };
  }, [items, formValues]);

  async function validateFields() {
    await form.validateFields();
  }

  const [sliceCount, setSliceCount] = useState(SLICE_COUNT);

  useEffect(() => {
    const body = document.querySelector('body');

    const onScroll = () => {
      if (body) {
        const rect = body.getBoundingClientRect();
        const scrollEnd = body.scrollHeight - rect.height;

        if (-rect.top + 200 >= scrollEnd) {
          setSliceCount((state) => state + SLICE_COUNT);
        }
      }
    };

    document.addEventListener('scroll', onScroll);

    return () => {
      document.removeEventListener('scroll', onScroll);
      setSliceCount(SLICE_COUNT);
    };
  }, []);

  window.onafterprint = () => {
    window.scrollTo({ top: 0 });

    setTimeout(() => {
      setSliceCount(prevSliceCount);
    }, 500);
  };

  function startPrint() {
    prevSliceCount = sliceCount;
    setSliceCount(items?.length || 0);

    setTimeout(window.print, 500);
  }

  return (
    <React.Suspense fallback={<>Carregando documento...</>}>
      {match.isExact && (
        <TemplateReport style={{ margin: '0px', width: '100%' }}>
          <>
            {(purchaseDatail.isLoading || companyDetail.isLoading) && (
              <ModalLoadingOverlay messages={['Carregando...']} />
            )}

            <Form
              form={form}
              initialValues={initialFormValues}
              onValuesChange={(_, values) =>
                localStorage.setItem(STORAGE_KEY, JSON.stringify(values))
              }
            >
              <main className="report-purchaseItem-tags">
                <div className="display-none-to-print">
                  <div className="report-purchaseItem-tags-header">
                    <Row justify="space-between" gutter={[20, 20]}>
                      <Col span={24} md={6}>
                        <h2 style={{ margin: 0, padding: 0 }}>
                          Gerar etiquetas de items da compra
                        </h2>
                      </Col>

                      <Col span={24} md={18}>
                        <SpaceForButton justifyContent="flex-end">
                          <Button block onClick={drawerConfigAction.open}>
                            Configurar impressão
                          </Button>

                          <Button
                            block
                            className="button-primary"
                            type="primary"
                            onClick={startPrint}
                          >
                            Imprimir
                          </Button>
                        </SpaceForButton>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="report-purchaseItem-tags-content">
                  {!purchaseId && (
                    <Empty description="Não exite o parâmetro ID de consulta da compra" />
                  )}

                  {!!purchaseId && isLoading && (
                    <ModalLoadingOverlay messages={['Carregando dados...']} />
                  )}

                  {!items?.length && !!purchaseId && (
                    <Empty description="Não foram encontrados itens na compra informada" />
                  )}

                  {!!items?.length && !!purchaseId && (
                    <>
                      <div className="print-page-info">
                        <span>
                          (
                          {hasPageSizeTypeCustom
                            ? 'Customizado'
                            : formValues?.pageTypeSize}
                          ){' '}
                          {`${
                            isLandscape
                              ? formValues?.pageSize?.y || 0
                              : formValues?.pageSize?.x || 0
                          }mmX${
                            isLandscape
                              ? formValues?.pageSize?.x || 0
                              : formValues?.pageSize?.y || 0
                          }mm`}{' '}
                          {formValues?.orientation === 'portrait'
                            ? 'Retrato'
                            : 'Paisagem'}
                        </span>
                      </div>

                      <div
                        className="print-area"
                        id="print-area"
                        style={{
                          paddingTop: `${formValues?.marginTop || 0}mm`,
                          paddingRight: `${formValues?.marginRight || 0}mm`,
                          paddingBottom: `${formValues?.marginBottom || 0}mm`,
                          paddingLeft: `${formValues?.marginLeft || 0}mm`,
                          width: formValues?.pageSize
                            ? `${
                                isLandscape
                                  ? formValues?.pageSize?.y || 0
                                  : formValues?.pageSize?.x || 0
                              }mm`
                            : undefined,
                        }}
                      >
                        <div
                          className="row"
                          style={{
                            margin: `0 -${(formValues?.gap || 0) / 2}mm`,
                            flexWrap: 'wrap',
                          }}
                        >
                          {items?.slice(0, sliceCount)?.map((item, i) => {
                            const contentColumns =
                              formValues?.contentColumns || 1;
                            const hasOneColumn = contentColumns === 1;

                            return (
                              <div
                                key={item?.id}
                                style={{
                                  margin: `0 ${(formValues?.gap || 0) / 2}mm 
                                ${formValues?.gap || 0}mm 
                                ${(formValues?.gap || 0) / 2}mm`,
                                  width: `${colWidthCalc}`,
                                  cursor: 'pointer',
                                }}
                                className="tag-wrapper"
                              >
                                <div
                                  className="card w-100 tag-card"
                                  style={{
                                    padding: 5,
                                    fontSize: formValues?.textSize,
                                    height: formValues?.cardHeightAuto
                                      ? 'auto'
                                      : `${formValues?.cardHeight}mm` || 'auto',
                                    borderColor: !formValues?.withCardBorder
                                      ? 'transparent'
                                      : undefined,
                                    textAlign: formValues?.textAlign || 'left',
                                    width: 'fit-content',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 8,
                                    }}
                                  >
                                    <div style={{ width: '100%' }}>
                                      {!!item.location && (
                                        <p
                                          style={{
                                            fontSize:
                                              (formValues?.textSize ?? 0) - 5,
                                          }}
                                        >
                                          {item.location}
                                        </p>
                                      )}

                                      <Row gutter={hasOneColumn ? 0 : 5}>
                                        {!!item?.barCode && (
                                          <Col span={24}>
                                            <div
                                              style={{
                                                display: hasOneColumn
                                                  ? 'inline'
                                                  : 'block',
                                              }}
                                            >
                                              {item?.barCode?.toUpperCase() !==
                                              'SEM GTIN' ? (
                                                <Barcode
                                                  value={item.partBarCode}
                                                  height={
                                                    (formValues?.textSize ??
                                                      0) * 1.5
                                                  }
                                                  fontSize={
                                                    (formValues?.textSize ??
                                                      0) - 2
                                                  }
                                                />
                                              ) : (
                                                'SEM GTIN'
                                              )}
                                            </div>
                                          </Col>
                                        )}

                                        <Col span={24 / contentColumns}>
                                          <div
                                            style={{
                                              display: hasOneColumn
                                                ? 'inline'
                                                : 'block',
                                              fontSize:
                                                (formValues?.textSize ?? 0) - 4,
                                            }}
                                          >
                                            <div style={{ marginTop: 3 }}>
                                              {item.name}
                                            </div>
                                          </div>
                                        </Col>
                                        <Col span={24 / contentColumns}>
                                          {formValues?.showSaleValue && (
                                            <div
                                              style={{
                                                display: hasOneColumn
                                                  ? 'inline'
                                                  : 'block',
                                                fontSize: formValues?.textSize,
                                              }}
                                            >
                                              <strong>
                                                {formatterPrice(
                                                  item.buyPrice ?? 0,
                                                )}
                                              </strong>
                                            </div>
                                          )}

                                          {formValues?.issueDate !== 'None' && (
                                            <div>
                                              <strong>
                                                {moment(item.issueDate).format(
                                                  formValues?.issueDate ||
                                                    'DD/MM/YYYY HH:mm:ss',
                                                )}
                                              </strong>
                                            </div>
                                          )}
                                        </Col>
                                        {formValues?.showCompanyImage && (
                                          <Col span={24}>
                                            <img
                                              width={
                                                formValues.companyImageResponsive
                                                  ? '100%'
                                                  : formValues.companyImageWidth ||
                                                    'auto'
                                              }
                                              height={
                                                formValues.companyImageResponsive
                                                  ? 'auto'
                                                  : formValues.companyImageHeight ||
                                                    'auto'
                                              }
                                              src={
                                                formValues?.companyImage ||
                                                companyDetail.data?.logo ||
                                                ''
                                              }
                                            />
                                          </Col>
                                        )}
                                      </Row>
                                    </div>
                                  </div>
                                </div>

                                {!formValues?.noLineBreak && (
                                  <>
                                    {!(
                                      (i + 1) %
                                      ((formValues?.cols || 0) *
                                        (formValues?.lineBreak || 0))
                                    ) && <div className="page-break" />}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </main>

              <Drawer
                title="Configuração de impressão"
                placement="right"
                open={drawerConfigOpen}
                onClose={drawerConfigAction.close}
                destroyOnClose={false}
                width={600}
              >
                <>
                  <div className="m-b-20">
                    <strong>Layout da página</strong>
                  </div>

                  <Row gutter={[16, 16]}>
                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Tipo de impressão"
                        name="pageTypeSize"
                        getValueFromEvent={(value) => {
                          if (value !== pageTypeSizes['Custom']) {
                            form.setFieldsValue({
                              pageSize: pageSizesType[value],
                            });
                          }
                          validateFields();
                          return value;
                        }}
                      >
                        <Select
                          options={pageTypeSizeOptions}
                          style={{ width: '100%' }}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Orientação" name="orientation">
                        <Select
                          options={[
                            { value: 'landscape', label: 'Paisagem' },
                            { value: 'portrait', label: 'Retrato' },
                          ]}
                          style={{ width: '100%' }}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Largura da página (mm)"
                        name={['pageSize', 'x']}
                        rules={[
                          {
                            type: 'number',
                            required: formValues?.pageTypeSize === 'Custom',
                            message: '',
                          },
                        ]}
                      >
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          disabled={!hasPageSizeTypeCustom}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Altura da página (mm)"
                        name={['pageSize', 'y']}
                        rules={[
                          {
                            type: 'number',
                            required: formValues?.pageTypeSize === 'Custom',
                            message: '',
                          },
                        ]}
                      >
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          disabled={!hasPageSizeTypeCustom}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Colunas" name="cols">
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={1}
                          max={10}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '1'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Espaço entre colunas (mm)"
                        name="gap"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '0'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        name="noLineBreak"
                        initialValue={true}
                        valuePropName="checked"
                        style={{ marginTop: 12 }}
                      >
                        <Checkbox>Quebra automática de linha</Checkbox>
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Linha de quebra"
                        name="lineBreak"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          disabled={formValues?.noLineBreak}
                          min={1}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '1'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>
                  </Row>

                  <Divider />

                  <div className="m-b-20">
                    <strong>Conteúdo</strong>
                  </div>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <FloatingFormItem
                        label="Alinhamento conteúdo"
                        name="textAlign"
                      >
                        <Select
                          style={{ width: '100%' }}
                          options={[
                            { value: 'left', label: 'Esquerda' },
                            {
                              value: 'center',
                              label: 'Centralizado',
                            },
                            {
                              value: 'right',
                              label: 'Direita',
                            },
                          ]}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <FloatingFormItem
                        label="Formato da data de emissão"
                        name="issueDate"
                      >
                        <Select
                          style={{ width: '100%' }}
                          options={[
                            {
                              value: 'DD/MM/YYYY HH:mm:ss',
                              label: 'DD/MM/YYYY HH:mm:ss',
                            },
                            {
                              value: 'DD/MM/YYYY HH:mm',
                              label: 'DD/MM/YYYY HH:mm',
                            },
                            { value: 'DD/MM/YYYY HH', label: 'DD/MM/YYYY HH' },
                            { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                            {
                              value: 'DD/MM/YY',
                              label: 'DD/MM/YY',
                            },
                            {
                              value: 'None',
                              label: 'Remover data',
                            },
                          ]}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Tamanho do texto (px)"
                        name="textSize"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={10}
                          max={24}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '10'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Caracteres da descrição"
                        name="descriptionSize"
                      >
                        <InputNumber<number>
                          style={{ width: '100%' }}
                          min={1}
                          formatter={(value) =>
                            value ? Math.floor(value).toString() : '1'
                          }
                          parser={(value) => Number(value)}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem
                        label="Colunas do conteúdo"
                        name="contentColumns"
                      >
                        <InputNumber style={{ width: '100%' }} min={1} />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <Form.Item name="showSaleValue" valuePropName="checked">
                        <Checkbox>Exibir preço do produto</Checkbox>
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="showCompanyImage"
                        valuePropName="checked"
                      >
                        <Checkbox>Exibir imagem da empresa</Checkbox>
                      </Form.Item>

                      {!!formValues?.showCompanyImage && (
                        <Row gutter={[16, 16]}>
                          <Col span={24}>
                            <Form.Item
                              name="companyImageResponsive"
                              valuePropName="checked"
                              getValueFromEvent={(value) => {
                                if (value.target.checked) {
                                  form.setFieldsValue({
                                    companyImageWidth: undefined,
                                    companyImageHeight: undefined,
                                  });
                                } else {
                                  form.setFieldValue('companyImageHeight', 80);
                                }
                                return value.target.checked;
                              }}
                            >
                              <Checkbox>Imagem responsiva</Checkbox>
                            </Form.Item>
                          </Col>

                          <Col span={24} md={12}>
                            <FloatingFormItem
                              label="Largura imagem (px)"
                              name="companyImageWidth"
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                disabled={formValues.companyImageResponsive}
                              />
                            </FloatingFormItem>
                          </Col>

                          <Col span={24} md={12}>
                            <FloatingFormItem
                              label="Altura imagem (px)"
                              name="companyImageHeight"
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                disabled={formValues.companyImageResponsive}
                              />
                            </FloatingFormItem>
                          </Col>

                          <Col span={24}>
                            <div style={{ position: 'relative' }}>
                              <Button block>Selecionar imagem</Button>
                              <input
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  opacity: 0,
                                  userSelect: 'none',
                                  pointerEvents: 'unset',
                                  cursor: 'pointer',
                                }}
                                type="file"
                                multiple={false}
                                max={1}
                                maxLength={1}
                                accept="image/png, image/jpg, image/jpeg"
                                onChange={({ target }) => {
                                  if (target.files?.length) {
                                    const image = URL.createObjectURL(
                                      target.files[0],
                                    );

                                    form.setFieldValue('companyImage', image);
                                  }
                                }}
                              />
                            </div>
                            {!!formValues.companyImage && (
                              <Button
                                danger
                                type="link"
                                onClick={() =>
                                  form.setFieldValue('companyImage', undefined)
                                }
                              >
                                Remover imagem selecionada
                              </Button>
                            )}
                          </Col>
                        </Row>
                      )}
                    </Col>

                    <Col span={24}>
                      <div className="m-b-20">
                        <strong>Etiqueta (card)</strong>
                      </div>

                      <Row gutter={[16, 16]}>
                        <Col span={24} md={12}>
                          <FloatingFormItem
                            name="cardHeightAuto"
                            initialValue={true}
                            valuePropName="checked"
                            style={{ marginTop: 12 }}
                          >
                            <Checkbox>Altura automática</Checkbox>
                          </FloatingFormItem>
                        </Col>

                        <Col span={24} md={12}>
                          <FloatingFormItem
                            label="Altura da etiqueta (mm)"
                            name="cardHeight"
                          >
                            <InputDecimal
                              disabled={formValues?.cardHeightAuto}
                              style={{ width: '100%' }}
                              min={0}
                            />
                          </FloatingFormItem>
                        </Col>

                        <Col span={24}>
                          <FloatingFormItem
                            name="withCardBorder"
                            initialValue={true}
                            valuePropName="checked"
                          >
                            <Checkbox>Aplicar borda a etiqueta</Checkbox>
                          </FloatingFormItem>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Divider />
                  <div className="m-b-20">
                    <strong>Margens da folha</strong>
                  </div>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <FloatingFormItem
                        label="Todo os lados (mm)"
                        name="allMarginSides"
                        getValueFromEvent={(value) => {
                          form.setFieldsValue({
                            marginBottom: value,
                            marginLeft: value,
                            marginRight: value,
                            marginTop: value,
                          });
                          return value;
                        }}
                      >
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <Row justify="center">
                        <Col span={12}>
                          <FloatingFormItem
                            label="Superior (mm)"
                            name="marginTop"
                          >
                            <InputDecimal
                              style={{ width: '100%' }}
                              min={0}
                              max={50}
                            />
                          </FloatingFormItem>
                        </Col>
                      </Row>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Esquerda (mm)" name="marginLeft">
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12}>
                      <FloatingFormItem label="Direita (mm)" name="marginRight">
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <Row justify="center">
                        <Col span={12}>
                          <FloatingFormItem
                            label="Inferior (mm)"
                            name="marginBottom"
                          >
                            <InputDecimal
                              style={{ width: '100%' }}
                              min={0}
                              max={50}
                            />
                          </FloatingFormItem>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </>

                <Divider />

                <div className="report-customer-tags-drawer-info m-b-20">
                  <strong>
                    <small>Legendas</small>
                  </strong>

                  <span className="-red">Área de impressão</span>

                  <span className="-gray">Quebra de página</span>
                </div>
              </Drawer>

              <Form.Item hidden name="cols" />
              <Form.Item hidden name="gap" />
              <Form.Item hidden name="pageTypeSize" />
              <Form.Item hidden name="pageSize" />
              <Form.Item hidden name="orientation" />
              <Form.Item hidden name="lineBreak" />
              <Form.Item hidden name="noLineBreak" />
              <Form.Item hidden name="textSize" />
              <Form.Item hidden name="descriptionSize" />
              <Form.Item hidden name="contentColumns" />
              <Form.Item hidden name="withCardBorder" />
              <Form.Item hidden name="displayCodes" />
              <Form.Item hidden name="displayBarcode" />
              <Form.Item hidden name="cardHeight" />
              <Form.Item hidden name="cardHeightAuto" />
              <Form.Item hidden name="margin" />
              <Form.Item hidden name="marginTop" />
              <Form.Item hidden name="marginBottom" />
              <Form.Item hidden name="marginLeft" />
              <Form.Item hidden name="marginRight" />
              <Form.Item hidden name="textAlign" />
              <Form.Item hidden name="issueDate" />
              <Form.Item hidden name="showSaleValue" />
              <Form.Item hidden name="showCompanyImage" />
              <Form.Item hidden name="companyImageWidth" />
              <Form.Item hidden name="companyImageHeight" />
              <Form.Item hidden name="companyImageResponsive" />
              <Form.Item hidden name="companyImage" />
            </Form>
          </>
        </TemplateReport>
      )}
    </React.Suspense>
  );
};

export default PrivateReportPurchaseItemTags;
